/**
 * balloonPressPipeline — a taut membrane surface that bulges where bass
 * pushes into it, like pressing fingers against a balloon.
 *
 * Thin-shell shading: extreme fresnel highlight at glancing angles,
 * transmission of a colored inner glow through the deformed apex, and
 * visible radial stress lines around each press site.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BalloonPressPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Three orbital press sites. Each one projects a Gaussian dimple onto
// the inflated sphere, scaled by its own sub-band.
float pressField(vec3 p) {
  float t = uTime;
  float d = 0.0;
  vec3 c1 = vec3(0.55 * cos(t * 0.43), 0.55 * sin(t * 0.39), 0.55 * sin(t * 0.27));
  vec3 c2 = vec3(0.55 * cos(t * 0.31 + 2.1), 0.55 * sin(t * 0.47 - 1.4), 0.55 * cos(t * 0.33));
  vec3 c3 = vec3(0.55 * sin(t * 0.51), 0.55 * cos(t * 0.29 + 1.7), 0.55 * sin(t * 0.45 + 0.8));
  float r1 = dot(p - c1, p - c1);
  float r2 = dot(p - c2, p - c2);
  float r3 = dot(p - c3, p - c3);
  d -= uBass.x * exp(-r1 * 7.0) * 0.42;
  d -= uBass.y * exp(-r2 * 7.0) * 0.42;
  d -= uBass.z * exp(-r3 * 7.0) * 0.42;
  return d;
}

float mapSurface(vec3 p) {
  // Base — slightly oblate sphere.
  float d = length(p / vec3(1.05, 1.05, 1.0)) - 1.0;
  // Press dimples.
  d += pressField(p);
  // Mid-driven radial stress rings around the press dimples.
  float stress = 0.0;
  float t = uTime;
  vec3 c1 = vec3(0.55 * cos(t * 0.43), 0.55 * sin(t * 0.39), 0.55 * sin(t * 0.27));
  vec3 c2 = vec3(0.55 * cos(t * 0.31 + 2.1), 0.55 * sin(t * 0.47 - 1.4), 0.55 * cos(t * 0.33));
  vec3 c3 = vec3(0.55 * sin(t * 0.51), 0.55 * cos(t * 0.29 + 1.7), 0.55 * sin(t * 0.45 + 0.8));
  stress += sin(length(p - c1) * 22.0 - t * 6.0) * 0.012;
  stress += sin(length(p - c2) * 26.0 - t * 7.0) * 0.012;
  stress += sin(length(p - c3) * 20.0 - t * 5.5) * 0.012;
  d += stress * (0.4 + (uMid.x + uMid.y) * 0.6);
  // High-band shimmer micro-bumps.
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  high = pow(clamp(high * 3.5, 0.0, 1.0), 0.85);
  d += sin(p.x * 35.0 + t * 2.5) * sin(p.y * 33.0 - t * 2.0) * 0.004 * high;
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.0015, 0.0);
  return normalize(vec3(
    mapSurface(p + e.xyy) - mapSurface(p - e.xyy),
    mapSurface(p + e.yxy) - mapSurface(p - e.yxy),
    mapSurface(p + e.yyx) - mapSurface(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 2.9, 0.0, 1.0), 0.75);
  high = pow(clamp(high * 3.5, 0.0, 1.0), 0.85);

  vec3 ro = vec3(0.0, 0.0, 3.6);
  vec3 rd = normalize(vec3(uv, -1.6));

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 90; i++) {
    float d = mapSurface(p);
    if (d < 0.0008) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.012, 0.018, 0.04);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.6, 0.7, 0.5));
    vec3 h = normalize(ldir - rd);
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 4.0);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 96.0);
    // Transmission — inner colored glow through the deformed apex.
    // Strongest where surface is most concave (apex of press dimple).
    float press = pressField(p);
    float trans = clamp(-press * 6.0, 0.0, 1.0);
    vec3 inner = mix(
      vec3(0.95, 0.4, 0.7),
      vec3(0.5, 0.85, 1.0),
      0.5 + 0.5 * sin(uTime * 0.5 + length(p) * 3.0)
    );
    col = vec3(0.0);
    col += diff * vec3(0.85, 0.82, 0.95);
    col += inner * trans * (0.4 + bass * 0.8);
    col += fres * vec3(0.7, 0.85, 1.0) * (0.7 + high * 1.6);
    col += spec * vec3(1.0) * (0.5 + bass * 1.0);
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeBalloonPressBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.9);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.7));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.5);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeBalloonPressRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createBalloonPressPipeline(gl: WebGL2RenderingContext): BalloonPressPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawBalloonPress(
  gl: WebGL2RenderingContext,
  pipe: BalloonPressPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}